import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { Seo } from '@/components/Seo';
import { Reveal } from '@/components/public/anim';
import { useLivePageContent } from '@/hooks/usePageContent';
import { projectDocsService, projectDocUrl } from '@/services/projectDocs.service';
import { ISTITUZIONALI } from '@/lib/institutionalLogos';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Leaf, Users, HeartHandshake, Sparkles, ArrowRight, Globe2, Award, ExternalLink, User, FileText, Download, ShieldCheck } from 'lucide-react';

const SLUG = 'il-progetto';
const CETS_URL = 'https://www.silaexperiences.com/p/progetto/cets-carta-europea-del-turismo-sostenibile-9577.html';
const PILLAR_ICONS = [HeartHandshake, Users, Leaf, Sparkles];

// Contenuti di DEFAULT (fallback): usati finché l'admin non inserisce override dal CRM.
const DEFAULTS = {
  eyebrow: 'Sila Experiences',
  title: 'Il Progetto',
  lead: 'Riscopri la tua natura, attraverso la Natura della Sila.',
  hero: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1920&q=80',
  intro: [
    'Il Palumbo Sila Experiences e l\u2019App Sila Experiences sono un progetto di responsabilità sociale d\u2019impresa condiviso con istituzioni pubbliche e private: un progetto di marketing turistico esperienziale che vuole riscrivere il rapporto Uomo-Natura.',
    'È un progetto di comunicazione di rete che mette in contatto te con circa 50 operatori — ristoratori, attività di divertimento, guide ambientali, professionisti del benessere olistico e dell\u2019avventura, istruttori di sopravvivenza, parchi avventura e astronomici, allevatori e produttori locali rigorosamente bio e a km.0 — uniti dalla missione di far scoprire la Sila in una veste nuova, sempre più legata alle proprie radici.',
    'Il legame con la Terra e la connessione con la Natura sono il fulcro di queste esperienze, che permetteranno a tutti — è questa la nostra sfida — di riconnettersi con la propria natura, attraverso la Natura della Sila.',
    'Attraverso le experiences proposte puntiamo al benessere dell\u2019Essere Umano, alle sue relazioni interpersonali e con il territorio, in un\u2019ottica olistica, sistemica, green ed etica: sulla creazione di comunità, sul risveglio delle coscienze, sul pensiero critico e sul bambino che è dentro ognuno di noi — capace di meravigliarsi, divertirsi, amare e sognare.',
  ].join('\n\n'),
  quote: '«Perché siamo tutti uniti e parte del Mondo, in questo misterioso cerchio che è la Vita.»',
  pillars: [
    { title: 'Responsabilità sociale', desc: 'Un progetto di CSR condiviso con istituzioni pubbliche e private.' },
    { title: 'Rete di ~50 operatori', desc: 'Ristoratori, guide ambientali, professionisti del benessere, produttori bio a km.0.' },
    { title: 'Green ed etico', desc: 'Un turismo olistico e sistemico che riscrive il rapporto Uomo-Natura.' },
    { title: 'Comunità e coscienza', desc: 'Risveglio delle coscienze, pensiero critico e meraviglia.' },
  ],
  sections: [
    { heading: 'CETS — Carta Europea del Turismo Sostenibile', body: 'Il Progetto Palumbo Sila Experiences, con le sue 12 azioni in diverse aree d\u2019intervento, nel 2023 si è conquistato il premio «Campione di Proposte» della CETS. La Carta è una dichiarazione di linee guida e best practices per i luoghi che vogliono costruire un turismo rispettoso dell\u2019ambiente, dei valori e delle tradizioni del territorio.\n\nA Maggio 2026 rappresenta Federparc Italia insieme al Parco Nazionale della Sila a un meeting internazionale in Lettonia per raccontare la propria esperienza CETS e il modello creato.\n\nA Giugno 2026 diventa Partner CETS.\n\nIl Parco Nazionale della Sila ha aderito alla CETS: un tavolo di lavoro ha permesso alle realtà delle 3 province di confrontarsi in un piano di azioni condiviso da svolgersi entro 5 anni.' },
    { heading: 'A chi si rivolge la CETS', body: 'Agli enti territoriali, per creare le condizioni di un turismo sostenibile.\nAlle imprese che operano nei territori aderenti alla Carta.\nAi turisti, invitati al rispetto dell\u2019ambiente e delle tradizioni locali.' },
  ],
  ctaTitle: 'Il più grande progetto di crowdfunding per la Sila',
  ctaText: 'Componi la tua esperienza di viaggio: risparmi, ti diverti e una parte della tua spesa viene investita per far crescere la Sila.',
  ctaLabel: 'Componi la tua experience',
  ctaTo: '/esperienze',
};

export default function IlProgetto() {
  const cms = useLivePageContent(SLUG);
  const [bilanci, setBilanci] = useState([]);

  useEffect(() => {
    let alive = true;
    projectDocsService.listBilanci('bilancio')
      .then((r) => { if (alive) setBilanci(r?.documents || []); })
      .catch(() => { if (alive) setBilanci([]); });
    return () => { alive = false; };
  }, []);

  const fmtSize = (b) => {
    if (!b && b !== 0) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const c = cms || {};
  const heroImage = c.heroImage || DEFAULTS.hero;
  const eyebrow = c.eyebrow || DEFAULTS.eyebrow;
  const title = c.title || DEFAULTS.title;
  const lead = c.lead || DEFAULTS.lead;
  const intro = (c.intro && c.intro.trim()) ? c.intro : DEFAULTS.intro;
  const paragraphs = intro.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const quote = (typeof c.quote === 'string' && c.quote.trim()) ? c.quote : DEFAULTS.quote;
  const pillars = Array.isArray(c.features) && c.features.length > 0 ? c.features : DEFAULTS.pillars;
  const sections = Array.isArray(c.sections) && c.sections.length > 0 ? c.sections : DEFAULTS.sections;
  const cta = {
    title: c.ctaTitle || DEFAULTS.ctaTitle,
    text: c.ctaText || DEFAULTS.ctaText,
    label: c.ctaLabel || DEFAULTS.ctaLabel,
    to: c.ctaTo || DEFAULTS.ctaTo,
  };

  return (
    <PublicLayout>
      <Seo title="Il Progetto" description="Palumbo Sila Experiences: un progetto di responsabilità sociale d'impresa e di crowdfunding per lo sviluppo della Sila. Riscopri la tua natura attraverso la Natura della Sila." path="/il-progetto" image={heroImage} />

      {/* HERO — stesso componente/forma di Alloggi (effetto Ken Burns + onda) */}
      <SilaHero image={heroImage} badge={eyebrow} title={title} subtitle={lead} icon={Globe2} testId="progetto-hero" />

      {/* MANIFESTO (testo giustificato) + box autrice (Chi sono) */}
      <section className="sila-section py-16 md:py-20" data-testid="progetto-manifesto">
        <div className="grid gap-10 lg:grid-cols-[300px_1fr] lg:gap-12">
          {/* Sidebar autrice — placeholder foto in attesa dell'immagine reale */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="progetto-autrice">
            <Card className="overflow-hidden">
              <div className="flex aspect-[4/5] w-full items-center justify-center bg-muted" data-testid="progetto-autrice-foto-placeholder">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <User className="h-12 w-12" />
                  <span className="text-xs font-medium">Foto in arrivo</span>
                </div>
              </div>
              <div className="p-5">
                <p className="font-display text-lg font-semibold">Ciao, sono Carmen Fiore</p>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  Creatrice dell'App Sila Experiences. Scopri di più su di me.
                </p>
                <Button asChild variant="outline" className="mt-4 w-full" data-testid="progetto-chi-sono-cta">
                  <Link to="/chi-sono">Chi sono <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </Card>
          </aside>

          {/* Manifesto */}
          <div className="space-y-5 text-[17px] leading-relaxed text-foreground/90">
            {paragraphs.map((p, i) => (
              <Reveal key={i}><p className="text-justify">{p}</p></Reveal>
            ))}
            {quote && (
              <Reveal>
                <blockquote className="border-l-4 border-primary pl-5 font-display text-2xl font-semibold italic text-foreground">
                  {quote}
                </blockquote>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* PILASTRI */}
      <section className="border-y border-border bg-muted/30 py-16" data-testid="progetto-pilastri">
        <div className="sila-section grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
            return (
              <Card key={i} className="p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-display text-lg font-semibold">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{p.desc || p.text}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* RICONOSCIMENTI */}
      <section className="sila-section py-16 md:py-20" data-testid="progetto-riconoscimenti">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Award className="h-3.5 w-3.5" />Riconoscimenti</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Premi e riconoscimenti</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Card className="flex flex-col p-6" data-testid="riconoscimento-bando">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Award className="h-3.5 w-3.5" />1º Premio Bando 2019</span>
            <h3 className="mt-4 font-display text-xl font-semibold">Parco Nazionale della Sila</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Il progetto è vincitore del 1º Premio del Bando 2019 del Parco Nazionale della Sila, di cui ha il Patrocinio.</p>
          </Card>
          <Card className="flex flex-col p-6" data-testid="riconoscimento-cets">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Sparkles className="h-3.5 w-3.5" />Campione di Proposte · 2023</span>
            <h3 className="mt-4 font-display text-xl font-semibold">CETS — Carta Europea del Turismo Sostenibile</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Premio «Campione di Proposte» della CETS. Da Giugno 2026 il progetto è <span className="font-semibold text-foreground">Partner CETS</span>.</p>
          </Card>
          <Card className="flex flex-col p-6" data-testid="riconoscimento-mab">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Globe2 className="h-3.5 w-3.5" />Piano d'Azione della Riserva della Biosfera Sila</span>
            <h3 className="mt-4 font-display text-xl font-semibold">Riserva della Biosfera Mab Unesco</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">A luglio 2026 il progetto entra nella Banca Progetti del Piano d'Azione della Riserva della Biosfera Sila, il documento strategico attraverso cui il nostro territorio definisce i progetti per concretizzare lo sviluppo sostenibile, in linea con gli obiettivi del Programma MAB UNESCO.</p>
          </Card>
        </div>
      </section>

      {/* PARTNER ISTITUZIONALI (stessi loghi del footer) */}
      <section className="border-y border-border bg-muted/30 py-14" data-testid="progetto-loghi">
        <div className="sila-section">
          <div className="mx-auto mb-8 max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Globe2 className="h-3.5 w-3.5" />Partner istituzionali</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Riconoscimenti e Partner Istituzionali</h2>
          </div>
          <ul className="mx-auto grid max-w-3xl grid-cols-2 items-stretch gap-3 sm:grid-cols-4 sm:gap-4" data-testid="progetto-loghi-list">
            {ISTITUZIONALI.map((it) => (
              <li key={it.slug}>
                <a
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={it.name}
                  title={it.name}
                  data-testid={`progetto-logo-${it.slug}`}
                  className="flex h-20 items-center justify-center rounded-xl border border-border bg-white p-3 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:h-24"
                >
                  <picture className="flex h-full w-full items-center justify-center">
                    <source srcSet={`/istituzionali/${it.slug}.webp`} type="image/webp" />
                    <img src={`/istituzionali/${it.slug}.png`} alt={it.name} loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" />
                  </picture>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TRASPARENZA — BILANCI (PDF caricati dall'admin) */}
      {bilanci.length > 0 && (
        <section className="sila-section py-16 md:py-20" data-testid="progetto-bilanci">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><ShieldCheck className="h-3.5 w-3.5" />Trasparenza</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Bilanci e documenti</h2>
            <p className="mt-2 text-sm text-muted-foreground">Consulta e scarica i documenti ufficiali del progetto.</p>
          </div>
          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2">
            {bilanci.map((doc) => (
              <a
                key={doc.id}
                href={projectDocUrl(doc.url)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`progetto-bilancio-${doc.id}`}
                className="group flex items-center gap-4 rounded-xl border border-border bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-6 w-6" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-foreground">{doc.title}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {doc.year ? `${doc.year} · ` : ''}PDF{doc.size ? ` · ${fmtSize(doc.size)}` : ''}
                  </span>
                </span>
                <Download className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="sila-section py-16 md:py-20">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <div className="space-y-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Award className="h-3.5 w-3.5" />Campione di Proposte</span>
            {sections.map((s, i) => (
              <Reveal key={i}>
                <div data-testid={`progetto-section-${i}`}>
                  {s.heading && <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{s.heading}</h2>}
                  {(s.body || '').split(/\n/).map((line, j) => line.trim() && (
                    <p key={j} className="mt-3 text-justify text-[15px] leading-relaxed text-muted-foreground">{line}</p>
                  ))}
                </div>
              </Reveal>
            ))}
            <Button asChild variant="outline" data-testid="progetto-cets-link">
              <a href={CETS_URL} target="_blank" rel="noopener noreferrer">Scopri la CETS <ExternalLink className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>

          <Reveal>
            <div className="sticky top-24 rounded-2xl bg-primary p-8 text-primary-foreground shadow-[var(--shadow-md)]">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-foreground/15"><Leaf className="h-5 w-5" /></span>
              <p className="mt-4 font-display text-xl font-semibold">{cta.title}</p>
              <p className="mt-2 text-sm text-primary-foreground/85">{cta.text}</p>
              <Button asChild variant="secondary" className="mt-5">
                <Link to={cta.to}>{cta.label} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </PublicLayout>
  );
}
